import { faImage } from '@fortawesome/pro-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import { Icon } from '../../../components/base/Icon';
import { ImageViewerModal } from '../../../components/ImageViewerModal';
import { useImageCache } from '../../../hooks/useImageCache';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { cn } from '../../../utils/cn';
import type {
	EnhancedFieldConfiguration,
	ListBuilderCallbacks,
} from '../../types';

interface ImageDisplayProps {
	field: EnhancedFieldConfiguration;
	value: any;
	data: any;
	listKey?: string;
	objects?: Array<{ objectTable: string; baseUrl?: string }>;
	callbacks?: ListBuilderCallbacks;
}

interface ImageSize {
	width: number;
	height: number;
}

const IMAGE_SIZES: Record<string, ImageSize | 'responsive'> = {
	thumbnail: { width: 40, height: 40 },
	small: { width: 60, height: 60 },
	medium: { width: 80, height: 80 },
	large: { width: 120, height: 120 },
	xlarge: { width: 160, height: 160 },
	responsive: 'responsive',
};

export function ImageDisplay({
	field,
	value,
	data,
	listKey,
	objects,
	callbacks,
}: ImageDisplayProps) {
	const colors = useThemeColors();
	const [imageError, setImageError] = useState(false);
	const [showViewer, setShowViewer] = useState(false);
	const [componentLoading, setComponentLoading] = useState(false);

	// Get configuration from field
	const imageConfig = field.styleConfig?.imageOptions || {};
	const imageSize = imageConfig.size || 'medium';
	const enableViewer = imageConfig.enableViewer !== false; // Default to true
	const sizeConfig = IMAGE_SIZES[imageSize] || IMAGE_SIZES['medium'];
	const isResponsive = sizeConfig === 'responsive';
	const dimensions = isResponsive ? null : (sizeConfig as ImageSize);

	// Convert value to string for URL
	const imageUrl = value ? String(value) : '';

	// Use image cache hook to check if image is already loaded
	const { isLoading, getCachedUrl, getError } = useImageCache(
		[imageUrl].filter(Boolean),
		{ preload: false },
	);

	// More conservative loading state approach
	// Only show loading if the component is explicitly loading and hasn't been loaded yet
	const imageLoading = componentLoading;

	// Reset states when URL changes
	useEffect(() => {
		if (imageUrl) {
			setImageError(false);
			// Only set loading to true initially, let onLoad/onError handle the rest
			setComponentLoading(true);
		} else {
			setComponentLoading(false);
		}
	}, [imageUrl]);

	// Handle image viewer
	const handleImageClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (enableViewer && imageUrl && !hasError) {
			setShowViewer(true);
		}
	};

	// If no URL, show placeholder
	if (!imageUrl) {
		const placeholderClasses = isResponsive
			? cn(
					'w-full aspect-square rounded-lg flex items-center justify-center border',
					colors.classes.bgSecondary,
					colors.classes.borderDefault,
				)
			: cn(
					'rounded-lg flex items-center justify-center border',
					colors.classes.bgSecondary,
					colors.classes.borderDefault,
				);

		const placeholderStyle = !isResponsive
			? {
					width: dimensions!.width,
					height: dimensions!.height,
				}
			: {};

		return (
			<div className={placeholderClasses} style={placeholderStyle}>
				<Icon
					icon={faImage}
					size={isResponsive ? 24 : dimensions!.width / 4}
					color={colors.values.textMuted}
				/>
			</div>
		);
	}

	// Get the best URL to use (cached or original)
	const displayUrl = imageUrl ? getCachedUrl(imageUrl) || imageUrl : '';
	const cacheError = imageUrl ? getError(imageUrl) : null;
	const hasError = imageError || !!cacheError;

	// Image viewer modal
	const renderImageViewer = (): React.ReactElement | null => {
		if (!showViewer) {
			return null;
		}

		return (
			<ImageViewerModal
				isOpen={showViewer}
				onClose={() => {
					setShowViewer(false);
				}}
				images={[{ url: displayUrl }]}
				initialIndex={0}
			/>
		);
	};

	// If there's an error, show placeholder
	if (hasError) {
		const placeholderClasses = isResponsive
			? cn(
					'w-full aspect-square rounded-lg flex items-center justify-center border',
					colors.classes.bgSecondary,
					colors.classes.borderDefault,
				)
			: cn(
					'rounded-lg flex items-center justify-center border',
					colors.classes.bgSecondary,
					colors.classes.borderDefault,
				);

		const placeholderStyle = !isResponsive
			? {
					width: dimensions!.width,
					height: dimensions!.height,
				}
			: {};

		return (
			<div className={placeholderClasses} style={placeholderStyle}>
				<Icon
					icon={faImage}
					size={isResponsive ? 24 : dimensions!.width / 4}
					color={colors.values.textMuted}
				/>
			</div>
		);
	}

	// Main image component
	const containerClasses = isResponsive
		? 'relative w-full aspect-square overflow-hidden rounded-lg'
		: 'relative overflow-hidden rounded-lg';

	const containerStyle = !isResponsive
		? {
				width: dimensions!.width,
				height: dimensions!.height,
			}
		: {};

	const imageClasses = isResponsive
		? 'absolute inset-0 w-full h-full object-cover'
		: 'block w-full h-full object-cover';

	const imageComponent = (
		<div className={containerClasses} style={containerStyle}>
			<img
				src={displayUrl}
				alt=""
				className={imageClasses}
				onLoad={(e) => {
					setComponentLoading(false);
				}}
				onError={() => {
					setComponentLoading(false);
					setImageError(true);
				}}
				ref={(imgRef) => {
					// Handle the case where the image is already loaded from browser cache
					// This can happen during hot reloads or when images are cached
					// Only update state if we're currently in loading state to prevent infinite loops
					if (
						imgRef &&
						imgRef.complete &&
						imgRef.naturalHeight !== 0 &&
						componentLoading
					) {
						setComponentLoading(false);
					}
				}}
			/>

			{/* Loading state - only show if component indicates loading */}
			{imageLoading && (
				<div
					className={cn(
						'absolute inset-0 flex items-center justify-center rounded-lg',
						colors.classes.bgSecondary,
					)}
				>
					<div
						className={cn(
							'animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500',
						)}
					/>
				</div>
			)}
		</div>
	);

	return (
		<>
			{enableViewer ? (
				<button
					onClick={handleImageClick}
					disabled={hasError}
					className={cn(
						hasError
							? 'cursor-default'
							: 'cursor-pointer hover:opacity-90 transition-opacity',
					)}
					type="button"
				>
					{imageComponent}
				</button>
			) : (
				imageComponent
			)}
			{renderImageViewer()}
		</>
	);
}
